import type { ResolvedFigure } from '../model.js';
import { goContextPropagation } from './go-context-propagation.js';
import { goMutexFix } from './go-mutex-fix.js';
import { goSliceAliasing } from './go-slice-aliasing.js';
import { assertSceneComposition, compileLessonFigure, type GoLesson, type LessonScene } from './types.js';

export type { GoLesson, LessonBeat, LessonFigureSpec, LessonScene, SceneSource, TruthReference } from './types.js';
export { assertSceneComposition, compileLessonFigure, COMPOSITION_CAPS, taughtElementOf } from './types.js';

/** The three Go lessons, in course order. */
export const GO_LESSONS: readonly GoLesson[] = [goSliceAliasing, goMutexFix, goContextPropagation];

/** The widths every scene must be correct at. */
export const SUPPORTED_WIDTHS = [320, 390, 640] as const;

/**
 * The width of the plate a figure is drawn on, derived from the harness shell's
 * own margins and padding. Kept here rather than measured in the DOM so the
 * width a scene compiles at is a pure function of the viewport.
 */
export function plateWidthFor(hostWidth: number): number {
  const narrow = hostWidth <= 780;
  const shell = Math.min(760, hostWidth - (narrow ? 24 : 32));
  return shell - (narrow ? 9 : 14) * 2 - 2;
}

/**
 * Snaps a host width onto a supported figure width, so what is tested is what
 * renders — and so an SVG never has to be scaled down to fit, which would take
 * its 11px labels below the floor.
 */
export function figureWidthFor(hostWidth: number): number {
  const plate = plateWidthFor(hostWidth);
  if (plate >= 640) return 640;
  if (plate >= 390) return 390;
  return 320;
}

export function lessonById(id: string): GoLesson | undefined {
  return GO_LESSONS.find((lesson) => lesson.id === id);
}

export function compileScene(scene: LessonScene, width: number): ResolvedFigure {
  return compileLessonFigure(scene.spec(width));
}

/** Compiles every scene of every lesson at one width, validating composition first. */
export function compileAllScenes(width: number): ResolvedFigure[] {
  const figures: ResolvedFigure[] = [];
  for (const lesson of GO_LESSONS) {
    for (const scene of lesson.scenes) {
      assertSceneComposition(lesson.id, scene);
      figures.push(compileScene(scene, width));
    }
  }
  return figures;
}

/** Flat index of every scene, used by the harness rail and by the test suite. */
export function allScenes(): Array<{ lesson: GoLesson; scene: LessonScene; lessonIndex: number; sceneIndex: number }> {
  return GO_LESSONS.flatMap((lesson, lessonIndex) =>
    lesson.scenes.map((scene, sceneIndex) => ({ lesson, scene, lessonIndex, sceneIndex })),
  );
}
